<template>
  <div class="application mt-5 mb-5">
    <b-container>
      <h2 class="section-heading mb-5">{{ page }}</h2>
      <b-row>
        <!-- Sidebar -->
        <b-col class="p-0" cols="12" lg="3">
          <div class="sidebar-container">
          <div class="sidebar shadow">
            <!-- Sidebar btns -->
            <div class="sidebar-link d-flex justify-content-between"
              v-for="(item, index) in links" :key="item.name + '-' + index"
              :class="{ active: getSectionOpen === index }"
              @click="handleClick(index)"
            >
              <div class="d-flex">
                <b-icon
                    :class="{'mobile-completed': getApplicationComplete[item.validationName] || getOnboardingComplete[item.validationName]}"
                    :icon="item.icon"
                    font-scale="1.3"
                    variant="secondary"
                />
                <p>{{ item.linkName }}</p>
              </div>
              <b-icon
                  v-if="getApplicationComplete[item.validationName] || getOnboardingComplete[item.validationName]"
                  variant="success"
                  icon="check"
                  font-scale="1.3"
                  class="completed-icon"/>
            </div>
            <div 
              v-if="!getApplicationReview.reviewApplication && !handleSaveLoading && getRecruit.stage === 'application'" 
              class="sidebar-link save-link mr-2 mr-md-0" 
              @click="handleSaveApplication"
            >
              Save<span class="desktop-text ml-1"> Progress</span>
            </div>
            <div 
              v-if="getSectionOpen !== 0 && !getApplicationReview.reviewApplication && !handleSaveLoading && getRecruit.stage === 'onboarding' && !onboardingDone" 
              class="sidebar-link save-link mr-2 mr-md-0" 
              @click="handleSaveOnboarding"
            >
              Save<span class="desktop-text ml-1"> Documents</span>
            </div>
            <div 
              v-if="getSectionOpen !== 0 && !getApplicationReview.reviewApplication && handleSaveLoading" 
              class="desktop-text"
            >
              <b-spinner label="Spinning" class="mt-3"  variant="success" />
            </div>
            <div 
              v-if="returnApplicationStatus && !onboardingDone && !editMode" 
              class="sidebar-link submit-link mr-2 mr-md-0" 
              @click="handleStartReview"
            >
              Review <span class="desktop-text ml-1">& Submit Application</span>
            </div>
            <div class="d-flex flex-column w-100" v-else-if="returnApplicationStatus && !onboardingDone && editMode">
                <div 
                  class="sidebar-link confirm-link mr-2 mr-md-0" 
                  @click="handleEdit"
                >
                  Edit Application
                </div>
                <div 
                  class="sidebar-link edit-link mr-2 mr-md-0" 
                  @click="handleSaveApplication('confirmSubmission')"
                >
                  Confirm Submission
                </div>
              </div>
            <div 
              v-if="returnOnboardingStatus && !onboardingDone" 
              class="sidebar-link submit-link" 
              @click="() => handleSaveOnboarding('submit')"
            >
              Submit <span class="desktop-text ml-1">Documents</span>
            </div>
            <div 
              class="sidebar-link logout-link" 
              @click="handleLogout"
            >
              Logout
            </div>
          </div>
          </div>
        </b-col>
        <!-- Main viewport -->
        <b-col class="p-0" cols="12" lg="9">
          <div class="main-view shadow">
            <slot></slot>
            <!-- Save progress btn -->
            <BtnMain
                v-if="(getSectionOpen !== 0 && !getApplicationReview.reviewApplication && !handleSaveLoading) && getRecruit.stage === 'application'"
                @btnClicked="handleSaveApplication('save-progress-bottom')"
                text="Save Progress" btnClass="save-btn mt-3" variant="success" />
                <BtnMain
                v-if="(getSectionOpen !== 0 && !getApplicationReview.reviewApplication && !handleSaveLoading) && getRecruit.stage === 'onboarding' && !onboardingDone"
                @btnClicked="() => handleSaveOnboarding('save-progress-bottom')"
                text="Save Documents" btnClass="save-btn mt-3" variant="success" />
            <b-container fluid v-if="getSectionOpen !== 0 && !getApplicationReview.reviewApplication && handleSaveLoading" class="ml-3 row">
              <b-spinner label="Spinning" class="justify-content-center mt-3"  variant="success" />
            </b-container>
          </div>

          <BtnMain
              v-if="returnApplicationStatus && !getApplicationReview.reviewApplication && !onboardingDone"
              @btnClicked="handleStartReview"
              text="Review & Submit Application" btnClass="save-btn mt-5" variant="info" />
          <!-- Confirm submission or edit, only visible at review application stage -->
          <div class="d-flex mt-5" v-if="getApplicationReview.reviewApplication">
            <BtnMain
                @btnClicked="() => handleSaveApplication('confirmSubmission')"
                text="Confirm Submission" btnClass="save-btn mr-3" variant="primary" />
            <BtnMain
                @btnClicked="handleEdit"
                text="Edit Application" btnClass="save-btn" variant="info" />
          </div>

        </b-col>
      </b-row>
    </b-container>
    <!-- Save success and error alerts -->
    <b-alert v-if="saveSuccess" class="save-alert m-2 d-flex flex-row align-items-center justify-content-center text-center" show variant="success">
      <p v-if="!submitSuccessMsg">You have successfully saved your progress.</p>
      <p v-else>{{ submitSuccessMsg }}</p>
    </b-alert>
    <b-alert v-if="saveError" class="save-alert m-2" show variant="danger">
      <p>{{ saveErrorMsg }}</p>
    </b-alert>
  </div>
</template>

<script>
import BtnMain from '../components/BtnMain.vue'
import { mapGetters, mapActions } from 'vuex'
import { saveApplication, saveOnboardingForms } from '../services/api'

export default {
  props: {
    page: String,
    links: Array
  },
  components: {
    BtnMain
  },
  computed: {
    ...mapGetters({
      getToken: 'authStore/getToken',
      getRecruit: 'authStore/getRecruit',
      getSectionOpen: 'authStore/getSectionOpen',
      getCurrentOnboardingForm: 'authStore/getCurrentOnboardingForm',
      getApplicationComplete: 'authStore/getApplicationComplete',
      getOnboardingComplete: 'authStore/getOnboardingComplete',
      isLoggedIn: 'authStore/isLoggedIn',
      getApplicationReview: 'authStore/getApplicationReview',
    }),
    returnApplicationStatus () {
      if (
        this.getApplicationComplete.personal &&
        this.getApplicationComplete.education &&
        this.getApplicationComplete.employment &&
        this.getApplicationComplete.work &&
        this.getApplicationComplete.why &&
        this.getRecruit.status !== 'submitted' &&
        this.getRecruit.stage !== 'onboarding'
      ) {
        return true
      } else {
        return false
      }
    },
    returnOnboardingStatus () {
      if (
        this.getOnboardingComplete.salary &&
        this.getOnboardingComplete.details &&
        this.getOnboardingComplete.starter &&
        this.getOnboardingComplete.acceptance &&
        this.getOnboardingComplete.corp &&
        this.getOnboardingComplete.faq &&
        (this.getOnboardingComplete.accommodation || !this.getRecruit.onboarding.documentsToShow.includes("accommodationInfo")) && // accommodation is sometimes removed from documents to show. Remove need to have this completed.
        this.getOnboardingComplete.induction &&
        this.getRecruit.stage === 'onboarding'
      ) {
        return true
      } else {
        return false
      }
    },
    onboardingDone(){
      const completeStatuses = ["onboardingComplete", "referencesChased", "referencesRecieved", "referencesRequested"];
      return completeStatuses.includes(this.getRecruit.status);
    }
  },
  data () {
    return {
      saveSuccess: false,
      saveError: false,
      saveErrorMsg: '',
      submitSuccessMsg: '',
      handleSaveLoading: false,
      editMode: false,
    }
  },
  methods: {
    ...mapActions({
      updateBelfastSelectedAction: 'authStore/updateBelfastSelectedAction',
      sectionOpenAction: 'authStore/sectionOpenAction',
      currentOnboardingFormAction: 'authStore/currentOnboardingFormAction',
      logoutAction: 'authStore/logoutAction',
      updateRecruitAction: 'authStore/updateRecruitAction',
      setStatusAction: 'authStore/setStatusAction',
      applicationReviewAction: 'authStore/applicationReviewAction',
      updateAuditTrailAction: 'authStore/updateAuditTrailAction',
      setStageAction: 'authStore/setStageAction'
    }),
    handleClick (section) {
      this.sectionOpenAction(section)
    },
    async handleSaveApplication (type) {
      this.handleSaveLoading = true     
      // Update Audit
      this.updateAuditTrailAction(this.getRecruit.auditTrail)
      // Check if belfast is selected and update accordingly
      if (this.getRecruit.centres.includes('Belfast')) {
        this.updateBelfastSelectedAction(true)
      } else {
        this.updateBelfastSelectedAction(false)
      }

      // Check if submission is being confirmed and update status
      if (type === 'confirmSubmission') {
        this.setStatusAction('submitted')
        window.scrollTo(0, 0)
      }
      // Set review data back to false
      this.applicationReviewAction({ input: 'reviewApplication', status: false })
      this.applicationReviewAction({ input: 'reviewApplicationComplete', status: false })
      this.editMode = false;
      const res = await saveApplication(this.getToken, this.getRecruit)
   
      if (res && res.status === 200 || res && res.success) {
        if (type === 'confirmSubmission') {
            // If submitting application redirect to success page
          this.$router.push('/application/ethnicity')
        }
        this.handleSaveLoading = false
        this.saveError = false
        this.saveSuccess = true
        window.scrollTo(0,0)
        if (type === 'save-progress-bottom'){
          // opens up next section of the application (unless it is the last section)
          let nextPageIndex = this.getSectionOpen + 1;
          if (nextPageIndex < this.links.length){
            setTimeout(() => {
              this.sectionOpenAction(nextPageIndex)
              }, 800)
            }
          }
        setTimeout(() => {
          this.handleSaveLoading = false
          this.saveError = false
          this.saveSuccess = false
        }, 5000)
      } else {
        this.handleSaveLoading = false
        this.saveSuccess = false
        this.saveError = true
        this.saveErrorMsg = 'Error saving, please try again'

        setTimeout(() => {
          this.handleSaveLoading = false
          this.saveSuccess = false
          this.saveError = false
        }, 5000)
      }

    },
    handleStartReview () {
      if (!this.getApplicationReview.reviewApplicationComplete) {
        this.editMode = true;
        this.applicationReviewAction({ input: 'reviewApplication', status: true })
        this.applicationReviewAction({ input: 'reviewApplicationComplete', status: true })
        window.scroll({
          top: 0,
          behavior: 'instant'
        })
        // End function execution here
        return { status: 'reviewing' }
      }
    },
    async handleSaveOnboarding (type) {
      try {
        let res
        if (type === 'submit') {
          const dataObj = {
            name: this.getCurrentOnboardingForm[this.getSectionOpen],
            data: this.getRecruit.onboarding.onboardingForms,
            onboardingComplete: this.getRecruit.onboarding.onboardingComplete,
            processComplete: true
          }

          res = await saveOnboardingForms(this.getToken, dataObj)
        } else {
          const dataObj = {
            name: this.getCurrentOnboardingForm[this.getSectionOpen],
            data: this.getRecruit.onboarding.onboardingForms,
            onboardingComplete: this.getRecruit.onboarding.onboardingComplete
          }
          res = await saveOnboardingForms(this.getToken, dataObj)
        }
        if (res && res.data && res.data.success) {
          if (type === 'submit') {
            this.saveError = false
            this.saveSuccess = true
            this.submitSuccessMsg = 'You have successfully submitted your onboarding documents and will now be logged out. You can log back into the portal at any time to view information regarding how to prepare for your induction training, information about your corporate gear and FAQs'
            window.scrollTo(0, 0)
            setTimeout(() => {
              this.saveSuccess = false
              this.submitSuccessMsg = ''
              this.logoutAction()
            }, 8000)
          } else {
            this.saveError = false
            this.saveSuccess = true
            this.submitSuccessMsg = 'Progress successfully updated'
            window.scrollTo(0, 0)
            if (type === 'save-progress-bottom'){
              // opens up next section of the application (unless it is the last section)
              let nextPageIndex = this.getSectionOpen + 1;
              if (nextPageIndex < this.links.length){
                setTimeout(() => {
                  this.sectionOpenAction(nextPageIndex)
                }, 800)
              }
            }
            setTimeout(() => {
              this.saveSuccess = false
              this.submitSuccessMsg = ''
            }, 3000)
          }
        } else {
          this.saveSuccess = false
          this.saveError = true
          this.saveErrorMsg = 'There was an error saving your progress, please refresh and try again'
          setTimeout(() => {
            this.saveError = false
            this.saveErrorMsg = ''
        }, 5000)
        }
      } catch (err) {
        this.saveSuccess = false
        this.saveError = true
        this.saveErrorMsg = 'There was an error saving your progress, please refresh and try again'
        setTimeout(() => {
            this.saveError = false
            this.saveErrorMsg = ''
        }, 5000)
      }
    },
    async handleLogout () {
      await this.logoutAction()
    },
    handleEdit () {
      this.applicationReviewAction({ input: 'reviewApplication', status: false })
      this.applicationReviewAction({ input: 'reviewApplicationComplete', status: false })
      this.sectionOpenAction(1)
      this.editMode = false;
    }
  }
}
</script>

<style scoped>
.sidebar-container {
  width: 100%;
}
.sidebar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 50px;
  z-index: 2;
  /* border: 1px solid #000; */
  border-bottom: 1px solid rgba(0, 0, 0, 0.39);
  background-color: #fff;
  padding: 20px;
}
.sidebar-link {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.sidebar-link:hover {
  cursor: pointer;
}
.sidebar-link {
  height: 100%;
  padding: 20px 0;
}
.sidebar-link p {
  display: none;
  margin: 0;
}
.main-view {
  height: auto;
  background-color: #fff;
  padding: 30px;
}
.save-link {
  color: #28a745;
}
.submit-link {
  color: #ffa409;
}
.save-alert {
    position: fixed;
    z-index: 100;
    bottom: 0;
    width: 100%;
    z-index: 10000;
  }
.edit-link {
  background-color: #357bfe;
  color: white
}
.confirm-link {
  background-color: #43a2b8;
  color: white;
}

@media only screen and (max-width: 820px) {
  .save-alert {
    position: fixed;
    z-index: 100;
    bottom: 0;
    width: 90%;
    margin-left: 5%;
    z-index: 10000;
  }
}
.sidebar-link.active .d-flex {
  border-bottom: 1px solid #333;
  padding-bottom: 10px;
}
.completed-icon {
  display: none !important;
}
.mobile-completed {
  color: #28a745 !important;
}
@media only screen and (min-width: 992px) {
  .sidebar-container {
    position: relative;
    /* width: 100%; */
  }
  .sidebar {
    /* position: fixed;
    z-index: 0; */
    height: auto;
    flex-direction: column;
    border: none;
    width: auto;
    padding: 0;
    margin-right: 20px;
  }
  .sidebar-link {
    justify-content: flex-start;
    transition: all .3s ease;
    width: 100%;
    padding: 15px 15px;
    border-bottom: 1px solid #000;
    font-size: 14px;
  }
  .sidebar-link:hover {
    background-color: #E2E3E5;
    cursor: pointer;
}
  .sidebar-link.active {
    background-color: #E2E3E5;
  }
  .sidebar-link p {
    display: block;
    padding-left: 10px;
  }
  .main-view {
    padding-top: 20px;
  }
  .save-link, .logout-link, .submit-link, .edit-link, .confirm-link {
    display: flex;
    justify-content: center;
    align-items: center;
    color: #fff;
    transition: all .3s ease;
  }
  .save-link {
    background-color: #28a745;
  }
  .logout-link {
    background-color: #343a40;
  }
  .submit-link {
    background-color: rgb(0, 174, 255);
  }
  .save-link:hover {
    background-color: #208a39;
  }
  .logout-link:hover {
    background-color: #1c1f22;
  }
  .submit-link:hover {
    background-color: rgb(0, 132, 194);
  }
  .sidebar-link.active .d-flex {
    border-bottom: none;
    padding-bottom: 0;
  }
  .completed-icon {
    display: block !important;
  }
  .mobile-completed {
    color: #000 !important;
  }
  .edit-link:hover {
    background-color: #1e60db;
  }
  .confirm-link:hover {
    background-color: #31879a;
  }
}
</style>
